import {defaultCssProperties} from '@lit-labs/motion';
import {not, or} from 'ramda';
import {combineLatest, of, map, NEVER, scan, tap} from 'rxjs';
import {Activity} from './activity';
import Decimal, {POSITIVE_INFINITY} from './break_infinity.js';
import {IValuable} from './interfaces/valuable';
import {Mods} from './modifier';
import {Resource} from './resource';
import {ri} from './runner-idle';
import {ResourceName} from './types/runner-idle';
import {
  fancy,
  sumActivitiesByTag,
  formatTime,
  capitalizeFirstLetter,
  all$,
  any$,
} from './utils';
import * as D from './decimal_utils';

export const resourceNames = [
  'null',
  'health',
  'healthExp',
  'stamina',
  'staminaExp',
  'focus',
  'mana',
  'manaExp',
  'manaDepth',
  'bookworms',
  'magicBookworms',
  'nutrition',
  'wood',
  'ironOre',
  'bait',
  'perch',
  'sunfish',
  'magicarp',
  'woodfish',
  'pemmican',
  'berry',
  'meat',
  'gel',
  'slimeMilk',
  'healthPotion',
  'cookedMeat',
  'time',
  'light',
  'fire',
  'lightCrystals',
  'timeCrystals',
] as const;

export function initializeResources() {
  /** ADD RESOURCES **/
  // Note if you're bughunting:
  // Caution! value has a setter that clamps to [0, capacity],
  // so capacity assignments must happen before value assignments
  // or the value can be clamped to the prior capacity.
  // Object.assign appears to use this order,
  // but this ri is sketchy voodoo.
  // SKETCHY FREAKING VOODOO. We are in the land of undefined behaviour and it is BAD.
  // TODO: Refactor (Fix, honestly.) to use arrays? Make a class for clamped values directly?

  // Any assignments go here
  function initResource(name: ResourceName, props: Partial<Resource>) {
    if (
      !['unlock$', 'requiredStats$', 'requiredResources$'].some(
        (prop) => prop in props
      )
    ) {
      props.requiredResources$ = of({[name]: `anyEver`});
    }
    ri.resources[name].setup({
      name,
      displayName: capitalizeFirstLetter(name),
      ...props,
    });
    if (props.level) {
      ri.resources[name].level$.next(props.level);
    }
    if (props.initialValue || props.initialValue) {
      ri.resources[name].resetToInitials();
    }
    if (props.capacityModifiers?.length) {
      ri.resources[name].capacityModifiers$.next(props.capacityModifiers);
    }
  }

  initResource('null', {unlock$: of(false), visible$: of(false)});

  initResource('health', {
    description:
      'Your health.<h2 style="margin:0px;color:orangered;"> If it reaches 0, you die!</h2>But death is not the end...',
    tags: new Set(['body']),
    initialCapacity: new Decimal(9),
    initialValue: new Decimal(10),
    onMinimum() {
      if (ri.tick.gt(1)) {
        ri.log(
          `<span class='danger'>You died.</span> Currently not implemented.`,
          ['death']
        );
        ri.resetSave();
      }
      // ri.reincarnate();
    },
    onLoss(delta: Decimal) {
      ri.resources.healthExp.addValue(delta.negate());
    },
    conditionString: 'Take damage for the first time',
    unlock$: ri.resources.health.full$.pipe(map(not), scan(or)),
    visible$: ri.resources.health.full$.pipe(map(not), scan(or)),
    // condition: () => ri.resources.health.value.lt(ri.resources.health.capacity),
    // visibleCondition: () =>
    //   ri.resources.health.value.lt(ri.resources.health.capacity),
    capacityModifiers: [
      Mods.plusLevel(ri.resources.health),
      Mods.timesLog2(ri.stats.vitality),
      Mods.floor(),
    ],
  });

  initResource('healthExp', {
    displayName: 'Health XP',
    description:
      "<strong>What doesn't kill you makes you stronger.</strong><br>Gain by taking damage.<br>Health Levels increase max Health.",
    tags: new Set(['body', 'experience']),
    baseCapacity: new Decimal(10),
    onMaxCapacity: () => {
      ri.resources.healthExp.setValue(0);
      ri.resources.health.levelUp();
      // ri.resources.healthExp.setValue(
      //   ri.resources.healthExp.value.minus(ri.resources.healthExp.capacity)
      // );
      // const oldCapacity = ri.resources.health.capacity;
      // ri.resources.health.setCapacity(ri.resources.health.baseCapacity.plus(1));
      // ri.log(
      //   `Increase Health Capacity by ${ri.resources.health.capacity.minus(
      //     oldCapacity
      //   )}!`,
      //   ['health', 'capacity']
      // );
      // ri.resources.healthExp.capacity = ri.resources.healthExp.capacity
      //   .times(1.1)
      //   .floor();
    },
    // onTick: () => {
    //   // HealthExp gain per second based on current health
    //   // "What doesn't kill you makes you stronger"
    //   if (ri.resources.health.value.lte(ri.resources.health.capacity)) {
    //     // ri.resources.healthExp = ri.resources.health.capacity - ri.resources.health.value
    //     const healthExpGained = ri.resources.health.vacancy.div(100);
    //     ri.resources.healthExp.value =
    //       ri.resources.healthExp.value.plus(healthExpGained);
    //     // logResource('healthExp', healthExpGained);
    //   }
    // },
    value: new Decimal(0),
    secret: true,
    conditionString: 'Unlock Health',
    unlock$: ri.resources.health.unlock$,
    visible$: ri.resources.health.unlock$,
    capacityModifiers: [
      Mods.timesPowLevel(
        1.3,
        ri.resources.health,
        (level) => level.minus(1),
        'level - 1'
      ),
      Mods.floor(),
    ],
  });

  initResource('nutrition', {
    displayName: 'Satiation',
    description: `Food fills you up! Goes down by 1 point every 5 seconds at base.
      <br>Using Stamina uses more.
      <br>There are no benefits or downsides to Satiation (for now),
      <br>it just limits how much food you can eat.`,
    tags: new Set(['body', 'food']),
    baseCapacity: new Decimal(100),
    value: new Decimal(0),
    onTick: () => {
      // Hunger
      if (ri.tick$.value.toNumber() % 5 == 0)
        ri.resources.nutrition.addValue(-1);
    },
    onMinimum: () => {
      // ri.log("You're starving! Find some food, quick!");
      // ri.gain('health', -1);
      // TODO add ascetic/masochistic rewards for hunger? monk stuff?
      // TODO!!!! Karma as a hidden multi-life luck element
    },
    // secret: true,
    // conditionString: `Find some food`,
    // unlock$: ri.activities.eat.unlock$,
  });

  initResource('wood', {
    name: 'wood',
    displayName: 'Wood',
    description:
      '<span class="fancy wood">Wood</span> is used to build things.<br>You can find <span class="fancy wood">Wood</span> in the forest (among other places).',
    tags: new Set(['wood', 'raw']),
    baseCapacity: new Decimal(10),
    value: new Decimal(0),
    secret: true,
    conditionString: 'Gather Wood',
    unlock$: ri.resources.wood.maxValue$.pipe(map(D.gt(0))),
    visible$: ri.activities.gather.unlock$,
    // visibleCondition: () =>
    //   ri.activities.gather.unlocked &&
    //   ri.activities.gather.options.includes('wood'),
    // capacityModifiers: [Mods.timesLog10(ri.stats.spaceMagic)],
  });

  initResource('ironOre', {
    name: 'ironOre',
    displayName: 'ironOre',
    description:
      '<span class="fancy ironOre">ironOre</span> is used to build things.<br>You can find <span class="fancy ironOre">ironOre</span> in the forest (among other places).',
    tags: new Set(['ironOre', 'raw']),
    baseCapacity: new Decimal(10),
    value: new Decimal(0),
    secret: true,
    conditionString: 'Gather ironOre',
    unlock$: ri.resources.ironOre.maxValue$.pipe(map(D.gt(0))),
    visible$: ri.activities.gather.unlock$,
    // visibleCondition: () =>
    //   ri.activities.gather.unlocked &&
    //   ri.activities.gather.options.includes('ironOre'),
    // capacityModifiers: [Mods.timesLog10(ri.stats.spaceMagic)],
  });

  initResource('meat', {
    name: 'meat',
    displayName: 'Meat',
    description:
      '<span class="fancy meat">Meat</span>, raw and bloody. I guess you could eat this?',
    tags: new Set(['food', 'raw']),
    baseCapacity: new Decimal(25),
    value: new Decimal(0),
    secret: true,
    unlock$: ri.resources.meat.maxValue$.pipe(map(D.gt(1))),
    // capacityModifiers: [Mods.timesLog10(ri.stats.spaceMagic)],
  });

  initResource('perch', {
    name: 'perch',
    displayName: 'Perch',
    description: `<span class="fancy perch">Perch</span>!
      Delicious and restores a little ${fancy('stamina', 'Stamina')}.
      Other fish like it too.`,
    tags: new Set(['food', 'raw', 'fish']),
    baseCapacity: new Decimal(100),
    value: new Decimal(0),
    secret: true,
    conditionString: `Get Perch`,
    unlock$: ri.resources.perch.maxValue$.pipe(map(D.gt(0))),
    visible$: ri.activities.fishing.unlock$,
    // capacityModifiers: [Mods.timesLog10(ri.stats.spaceMagic)],
  });

  initResource('sunfish', {
    name: 'sunfish',
    displayName: 'Sunfish',
    description: `<span class="fancy sunfish">sunfish</span>!
      Brightens up your day!
      Other fish like it too.`,
    tags: new Set(['food', 'raw', 'fish']),
    baseCapacity: new Decimal(100),
    value: new Decimal(0),
    secret: true,
    conditionString: `Get Sunfish`,
    unlock$: ri.resources.sunfish.maxValue$.pipe(map(D.gt(0))),
    visible$: ri.resources.sunfish.maxValue$.pipe(map(D.gt(0))),
    // capacityModifiers: [Mods.timesLog10(ri.stats.spaceMagic)],
  });

  initResource('magicarp', {
    name: 'magicarp',
    displayName: 'Magicarp',
    description: `<span class="fancy magicarp">Magicarp</span>!
      Magical! Does not transform.
      Other fish like it too.`,
    tags: new Set(['food', 'raw', 'fish']),
    baseCapacity: new Decimal(100),
    value: new Decimal(0),
    secret: true,
    conditionString: `Get Magicarp`,
    unlock$: ri.resources.magicarp.maxValue$.pipe(map(D.gt(0))),
    visible$: ri.resources.magicarp.maxValue$.pipe(map(D.gt(0))),
  });

  initResource('woodfish', {
    name: 'woodfish',
    displayName: 'Woodfish',
    description: `<span class="fancy woodfish">Woodfish</span>!
      Magical! Does not transform.
      Other fish like it too.`,
    tags: new Set(['food', 'raw', 'fish']),
    baseCapacity: new Decimal(100),
    value: new Decimal(0),
    secret: true,
    conditionString: `Get Woodfish`,
    unlock$: ri.resources.woodfish.maxValue$.pipe(map(D.gt(0))),
    visible$: ri.resources.woodfish.maxValue$.pipe(map(D.gt(0))),
  });

  initResource('bait', {
    name: 'bait',
    displayName: 'Bait',
    description: `<span class="fancy bait">Bait</span>, raw and baity.
      <br>${fancy('perch', 'Fish')} can't get enough of the stuff.`,
    tags: new Set([]),
    baseCapacity: new Decimal(100),
    value: new Decimal(0),
    secret: true,
    conditionString: `Get bait`,
    unlock$: ri.resources.bait.maxValue$.pipe(map(D.gt(0))),
    visible$: ri.resources.bait.maxValue$.pipe(map(D.gt(0))),
    // capacityModifiers: [Mods.timesLog10(ri.stats.spaceMagic)],
  });

  initResource('cookedMeat', {
    name: 'cookedMeat',
    displayName: 'Cooked Meat',
    description:
      'A well-cooked (and not well-done) piece of <span class="fancy cookedMeat">Cooked Meat</span>. Much better than the raw stuff.',
    tags: new Set(['food', 'cooked']),
    baseCapacity: new Decimal(25),
    value: new Decimal(0),
    secret: true,
    conditionString: `Make it medium-rare`,
    unlock$: ri.resources.cookedMeat.maxValue$.pipe(map(D.gt(0))),
    visible$: ri.resources.cookedMeat.maxValue$.pipe(map(D.gt(0))),
    // capacityModifiers: [Mods.timesLog10(ri.stats.spaceMagic)],
  });

  initResource('pemmican', {
    name: 'pemmican',
    displayName: 'Pemmican',
    description:
      'A packed together mixture of ground-up dried <span class="fancy meat">Meat</span>, fat, and mystery <span class="fancy berry">Berries</span>.<br>Very nutritious!',
    tags: new Set(['food', 'cooked']),
    baseCapacity: new Decimal(100),
    secret: true,
    conditionString: `Mix some meat and berries`,
    unlock$: ri.resources.pemmican.maxValue$.pipe(map(D.gt(0))),
    visible$: all$([ri.resources.meat.unlock$, ri.resources.berry.unlock$]),
    // capacityModifiers: [Mods.timesLog10(ri.stats.spaceMagic)],
  });

  initResource('gel', {
    name: 'gel',
    displayName: 'Gel',
    tags: new Set(['food']),
    baseCapacity: new Decimal(10),
    value: new Decimal(0),
    secret: true,
  });

  initResource('slimeMilk', {
    name: 'slimeMilk',
    displayName: 'Slime Milk',
    tags: new Set(['food', 'potion']),
    baseCapacity: new Decimal(10),
    value: new Decimal(0),
    secret: true,
  });

  initResource('healthPotion', {
    name: 'healthPotion',
    displayName: 'Health Potion',
    tags: new Set(['food', 'potion']),
    baseCapacity: new Decimal(10),
    value: new Decimal(0),
    secret: true,
  });

  initResource('berry', {
    name: 'berry',
    displayName: 'Berries',
    tags: new Set(['food']),
    description: `Some wild <span class="fancy berry">Berries</span>. Nutritious and delicious!<br>They make you feel a little strange.`,
    baseCapacity: new Decimal(100),
    value: new Decimal(0),
    secret: true,
    // unlock$: ri.resources.berry.maxValue$.pipe(map(D.gte(1))),
    // capacityModifiers: [Mods.timesLog10(ri.stats.spaceMagic)],
  });

  // Stamina
  initResource('stamina', {
    name: 'stamina',
    displayName: 'Stamina',
    description: `Physical Activities cost ${fancy(
      'stamina',
      'Stamina'
    )}.<br>Restore by ${fancy(
      'rest',
      'Resting'
    )}.<br>Gain Stamina XP to level up and gain capacity.`,
    tags: new Set(['body', 'train']),
    initialCapacity: new Decimal(0),
    initialValue: new Decimal(1),
    unlock$: of(true),
    onLoss: (_newValue: Decimal) => {
      ri.resources.nutrition.addValue(
        -1
        // ri.resources.stamina.value.minus(_newValue.max(0)).div(10)
      );
    },
    capacityModifiers: [
      Mods.plusLevel(ri.resources.stamina),
      Mods.timesLog2(ri.stats.endurance),
      Mods.floor(),
    ],
  });

  //Stamina XP
  initResource('staminaExp', {
    name: 'staminaExp',
    displayName: 'Stamina XP',
    description: 'Stamina level increases on max XP.',
    tags: new Set(['body', 'train', 'experience']),
    initialCapacity: new Decimal(5),
    initialValue: new Decimal(0),
    unlock$: of(true),
    onMaxCapacity: () => {
      // Empty ri
      ri.resources.staminaExp.baseValue$.next(new Decimal(0));
      // Level up
      ri.resources.stamina.levelUp();
    },
    capacityModifiers: [
      Mods.timesPowLevel(
        1.4,
        ri.resources.stamina,
        (level) => level.minus(1),
        'level - 1'
      ),
      Mods.floor(),
    ],
  });

  // Focus
  initResource('focus', {
    name: 'focus',
    displayName: 'Focus',
    description: `In the ZONE. Increases Training effectiveness.<br>Push your limits to improve.`,
    tags: new Set(['body', 'train']),
    baseCapacity: new Decimal(100),
    value: new Decimal(0),
    onTick: (performed: boolean, activity: Activity) => {
      if (performed && activity.tags.has('train')) {
        // Gain focus capacity by training at max focus
        if (ri.resources.focus.value.gte(ri.resources.focus.capacity)) {
          ri.resources.focus.baseCapacity =
            ri.resources.focus.baseCapacity.plus(0.1);
        }
        ri.resources.focus.addValue(1);
      } else {
        // Focus drops by 1/s when not training
        ri.resources.focus.addValue(-1);
      }
    },
    conditionString: 'Spend one minute Training at the Gym',
    unlock$: D.sum$([
      ri.activities.trainStrength.timeSpent$,
      ri.activities.trainSpeed.timeSpent$,
      ri.activities.trainStrSpd.timeSpent$,
    ]).pipe(map(D.gte(60))),
    visible$: ri.landmarks.gym.visited$,
    // condition: () => sumActivitiesByTag('train', 'timeSpent').gte(300),
    // progress: () => sumActivitiesByTag('train', 'timeSpent').div(300),
    // // Visible if some 'train' activity is unlocked
    // visibleCondition: () =>
    //   Object.values(ri.activities).some(
    //     (activity: Activity) => activity.tags.has('train') && activity.unlocked
    //   ),
    // capacityModifiers: [Mods.ceil()],
  });

  initResource('mana', {
    name: 'mana',
    displayName: 'Mana',
    description: 'Magical energy. Used for Spells.',
    tags: new Set(['mind', 'meditate']),
    baseCapacity: new Decimal(1),
    value: new Decimal(0),
    onGain: (_newValue: Decimal) => {
      ri.resources.manaExp.value = ri.resources.manaExp.value.plus(
        _newValue.minus(ri.resources.mana.value)
      );
    },
    secret: true,
    conditionString: 'Get Mana',
    unlock$: ri.resources.mana.maxValue$.pipe(map(D.gt(0))),
    visible$: ri.resources.mana.maxValue$.pipe(map(D.gt(0))),
    persistentUnlock: true,
  });
  Object.assign(ri.resources.mana, {
    // capacityModifiers: [
    //   Mods.plusLevel(ri.resources.mana),
    //   Mods.timesLog10(ri.stats.wisdom as IValuable),
    //   Mods.floor(),
    // ],
  });

  // Mana XP
  initResource('manaExp', {
    name: 'manaExp',

    displayName: 'Mana XP',
    description:
      'Gain Mana XP by gaining Mana.<br>Mana capacity increases on max XP.',
    tags: new Set(['mind', 'meditate', 'experience']),
    baseCapacity: new Decimal(10),
    value: new Decimal(0),
    secret: true,
    conditionString: 'Unlock Mana',
    unlock$: ri.resources.mana.unlock$,
    visible$: any$([ri.resources.mana.unlock$, ri.resources.manaDepth.unlock$]),
    persistentUnlock: true,
    onMaxCapacity: () => {
      // Empty ri
      ri.resources.manaExp.value = new Decimal(0);
      // Level up
      ri.resources.mana.levelUp();
    },
    // capacityModifiers: [
    //   Mods.timesPowLevel(1.5, ri.resources.mana),
    //   Mods.floor(),
    // ],
  });

  // Mana Depth
  initResource('manaDepth', {
    name: 'manaDepth',
    displayName: 'Mana Density',
    description: `The density of the Mana around you. Improves the potential of Meditation.<br>Increases while Meditating and goes down otherwise.`,
    tags: new Set(['mind', 'meditate']),
    baseCapacity: new Decimal(1e3),
    value: new Decimal(0),
    onTick: (performed: boolean, activity: Activity) => {
      // Doing meditation activities adds manaDepth, which depletes otherwise a
      if (performed && activity.tags.has('meditate')) {
        ri.gain('manaDepth', 1);
      } else if (performed && activity.tags.has('spell')) {
        ri.resources.manaDepth.value = ri.resources.manaDepth.value.minus(1);
      } else {
        ri.resources.manaDepth.value = ri.resources.manaDepth.value
          .times(0.9)
          .floor();
      }
    },

    secret: true,
    conditionString: 'Meditate 10 times in a row',
    // condition: () => ri.activities.meditate.consecutiveUses.gte(10),
    // progress: () => ri.activities.meditate.consecutiveUses.div(10),
    unlock$: ri.activities.meditate.consecutiveUses$.pipe(map(D.gte(10))),
    visible$: ri.activities.meditate.unlock$,
    persistentUnlock: true,
  });

  initResource('bookworms', {
    name: 'bookworms',
    displayName: 'Bookworms',
    description:
      'The common bookworm (<em>Bibliophagus tetrimmeno</em>). Tiny creatures, they absorb knowledge from the books they eat.',
    tags: new Set(['food', 'raw']),
    baseCapacity: new Decimal(1000),
    value: new Decimal(0),
    secret: true,
    // capacityModifiers: [Mods.timesLog10(ri.stats.spaceMagic)],
  });

  initResource('magicBookworms', {
    name: 'magicBookworms',
    displayName: 'Arcane Bookworms',
    description: `The arcane bookworm (<em>Bibliophagus arcanum</em>)
      <br>Like their common cousins, they absorb knowledge from the books they eat,
      <br>but the arcane bookwork does something with it...`,
    tags: new Set(['food', 'raw']),
    baseCapacity: new Decimal(1000),
    value: new Decimal(0),
    secret: true,
    // capacityModifiers: [Mods.timesLog10(ri.stats.spaceMagic)],
  });

  // // Sanity
  // initResource('sanity', {
  //   name: 'sanity',
  //   displayName: 'Sanity',
  //   tags: new Set(['mind']),
  //   capacity: new Decimal(10),
  //   value: new Decimal(10),
  //   secret: true,
  //   conditionString: 'Go insane',
  //   condition: () => ri.resources.sanity.value.equals(0),
  //   visibleCondition: () => ri.resources.sanity.value.equals(0),
  //   persistentUnlock: true,
  //   persistentValue: true,
  // });

  // Time Crystals
  initResource('timeCrystals', {
    name: 'timeCrystals',
    displayName: 'Time Crystals',
    description: `Solid Time!`,
    tags: new Set(['time', 'special']),
    baseCapacity: POSITIVE_INFINITY,
    value: new Decimal(0),
    valueFormatter: formatTime,
    capacityFormatter: () => 'Infinite',
    secret: true,
    // persistentUnlock: true,
  });

  // Time
  initResource('time', {
    name: 'time',
    displayName: 'Time',
    description: `Bottled Time! The basic component of all Time Magic.<br><br>Ironically, smells like oregano.`,
    tags: new Set(['special']),
    baseCapacity: new Decimal(1800),
    value: new Decimal(0),
    valueFormatter: formatTime,
    capacityFormatter: formatTime,
    secret: true,
    conditionString: 'Save 60 seconds of Time',
    unlock$: ri.resources.time.maxValue$.pipe(map(D.gte(60))),
    visible$: ri.resources.time.maxValue$.pipe(map(D.gte(1))),
    // progress: () => ri.resources.time.value.div(60),
    persistentUnlock: true,
    capacityModifiers: [Mods.plusPer(1, ri.resources.timeCrystals)],
  });

  // Light
  initResource('light', {
    name: 'light',
    displayName: 'Light',
    description: `How bright it is in your immediate vicinity.
        <br>0 represents the normal ambient light around you.
        <br>-100 is complete darkness,
        <br>100 is extremely bright.`,
    tags: new Set(['special']),
    baseCapacity: new Decimal(100),
    minimum: new Decimal(-100),
    value: new Decimal(0),
    onTick: () => {
      // Light crystals emit some light
      ri.resources.light.value = ri.resources.light.value.plus(
        ri.resources.lightCrystals.value.times(0.01)
      );
      // Light moves towards 0 over time
      ri.resources.light.value = ri.resources.light.value.minus(
        ri.resources.light.value.sign()
      );
    },
    secret: true,
    conditionString: 'Change the light level',
    unlock$: ri.resources.light.maxValue$.pipe(map(D.neq(0))),
    visible$: ri.resources.light.unlock$,
  });

  // light Crystals
  initResource('lightCrystals', {
    name: 'lightCrystals',
    displayName: 'Light Crystals',
    description: `Solid light.`,
    tags: new Set(['light', 'special']),
    baseCapacity: POSITIVE_INFINITY,
    value: new Decimal(0),
    capacityFormatter: () => 'Infinite',
    secret: true,
    conditionString: 'Gather 60 Light',
    unlock$: ri.resources.light.maxValue$.pipe(map(D.gte(60))),
    visible$: ri.resources.light.maxValue$.pipe(map(D.gte(1))),
    // progress: () => ri.resources.light.value.div(60),
    persistentUnlock: true,
  });

  // Fire
  initResource('fire', {
    name: 'fire',
    displayName: 'Fire',
    description: `Fire! Keep it burning and it'll keep growing, but stop and it'll go out!`,
    tags: new Set(['special']),
    baseCapacity: new Decimal(100),
    value: new Decimal(0),
    // onTick: () => {
    //   const fire = ri.resources.fire;
    //   // Grow/shrink the fire capacity based on current fullness
    //   if (fire.value.gt(fire.capacity.div(2))) {
    //     fire.capacity = fire.capacity.plus(fire.value.div(10));
    //   } else if (fire.capacity.gt(10)) {
    //     fire.capacity = fire.capacity.minus(fire.vacancy.div(10));
    //   }
    //   // Let off some Light
    //   if (Math.random() < 0.01 * fire.value.toNumber()) {
    //     ri.resources.light.value = ri.resources.light.value.plus(
    //       fire.value.div(10).floor()
    //     );
    //   }
    //   // // sometimes burn some wood
    //   // if (wood.value.gt(0)) {
    //   //   if (Math.random() < 0.01 * fire.value.toNumber()) {
    //   //     wood.value = wood.value.minus(1);
    //   //     fire.capacity = Decimal.max(fire.capacity, fire.value.plus(10));
    //   //     fire.value = fire.value.plus(10);
    //   //   }
    //   // }
    //   fire.value = fire.value.minus(fire.value.times(0.05).max(0.001));
    // },
    secret: true,
    conditionString: 'Light a fire',
    unlock$: ri.resources.fire.maxValue$.pipe(map(D.gt(0))),
    visible$: ri.resources.fire.unlock$,
  });

  // // Mana Light
  // initResource('manaLight', {
  //   name: 'manaLight',Currently the game seems to be having some rendering issues causing lag, especially after long play sessions. If you're having ri issue, I'd recommend refreshing the page temporarily, but it also
  //   displayName: 'Magic Lightbulb',
  //   description: 'Create light from mana.',
  //   baseCapacity: new Decimal(100),
  //   value: new Decimal(0),
  //   onTick: () => {
  //     ri.resources.light.value = ri.resources.light.value.plus(
  //       ri.resources.manaLight.value
  //     );
  //     ri.resources.mana.value = ri.resources.mana.value.minus(
  //       ri.resources.manaLight.value
  //     );
  //   },
  // });

  // // Solar Panel
  // initResource('solarPanel', {
  //   name: 'solarPanel',
  //   displayName: 'Magic Solar Panel',
  //   description: 'Absorb light to create mana',
  //   baseCapacity: new Decimal(100),
  //   value: new Decimal(0),
  //   onTick: () => {
  //     ri.resources.light.value = ri.resources.light.value.minus(
  //       ri.resources.solarPanel.value
  //     );
  //     ri.resources.mana.value = ri.resources.mana.value.plus(
  //       ri.resources.solarPanel.value
  //     );
  //   },
  // });
}
